import { useMemo } from 'react';
import { Building2 } from 'lucide-react';
import { getActiveDrillItemPath, getActiveNavItemLabel } from '../NavBar/nav-utils';
import {
  IdentityHeader,
  IdentityTile,
  TakeoverAside,
  TakeoverNavGroups,
  TakeoverSearchField,
  takeoverShellClassName,
  useTakeoverNavSearch,
} from '../TakeoverShell';
import type { ConfigureNavGroup } from './configure-nav';

export interface ConfigureShellViewProps {
  groups: ConfigureNavGroup[];
  /** Current pathname, used to highlight the active nav entry. */
  activePath: string;
  /** Organization shown on the identity header. */
  orgName: string;
  orgAvatarUrl?: string | null;
  onBack: () => void;
  children: React.ReactNode;
}

/**
 * Presentational layout of the Configure takeover: a dedicated sidebar
 * beside the config page content. One flat level — Back chip, org identity
 * header, search, and grouped nav rows. `esc` mirrors the chip and always
 * leaves to the agent sessions page. The connected {@link ConfigureShell}
 * wires nav sources, routing, and the escape/back behavior; stories drive
 * this view directly.
 *
 * On mobile a header shows the current page label and a button that opens
 * the aside as a sheet, matching the agent stream.
 */
export function ConfigureShellView({
  groups,
  activePath,
  orgName,
  orgAvatarUrl,
  onBack,
  children,
}: ConfigureShellViewProps) {
  const { query, setQuery, filterGroups } = useTakeoverNavSearch();

  const navItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const activeItemPath = useMemo(
    () => getActiveDrillItemPath(navItems, activePath),
    [navItems, activePath],
  );
  const pageTitle = getActiveNavItemLabel(navItems, activePath, 'Configure');

  return (
    <div className={takeoverShellClassName} data-testid="configure-shell">
      <TakeoverAside
        back={{ label: 'Back', onClick: onBack, testId: 'configure-back' }}
        header={
          <IdentityHeader
            tile={<IdentityTile avatarUrl={orgAvatarUrl} icon={Building2} />}
            title="Configure"
            subtitle={orgName}
          />
        }
        pageTitle={pageTitle}
        page={<div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>}
      >
        <TakeoverSearchField query={query} setQuery={setQuery} placeholder="Search configure" />
        <nav
          aria-label="Configure navigation"
          className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pb-3 scrollbar-fade"
        >
          <TakeoverNavGroups
            groups={filterGroups(groups)}
            activeItemPath={activeItemPath}
            testIdPrefix="configure-nav"
          />
        </nav>
      </TakeoverAside>
    </div>
  );
}
